/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * First run — three questions, then a week that already exists.
 *
 * ── THE PROBLEM ─────────────────────────────────────────────────────────────
 * A new household used to arrive at an empty dashboard and a four-item
 * checklist: add people, add a recipe, plan the week, build a list. All four
 * are chores, none of them shows what the app is for, and the payoff — a
 * planned week and a shopping list that writes itself — sits on the far side of
 * all of them. Most people do not get there.
 *
 * ── THE INVERSION ───────────────────────────────────────────────────────────
 * Ask three questions, then DO the four chores. The user's first experience of
 * MealPlanner is a full week with a shopping list attached, which they can then
 * change — and editing something that exists is a vastly easier task than
 * creating something from nothing. It also demonstrates the entire loop
 * (recipes → plan → list) in about ninety seconds, which no amount of
 * onboarding copy achieves.
 *
 * ── NO NEW SERVER WORK ──────────────────────────────────────────────────────
 * Every step composes an endpoint that already exists and is already used
 * elsewhere: SettingsApi.addDiet, AiApi.generate, RecipesApi.create,
 * PlanApi.add, ShoppingApi.generate. This is a client-side orchestration of
 * capabilities the product already shipped — which is rather the point of the
 * audit finding it was missing.
 *
 * ── FAILURE IS EXPECTED, NOT EXCEPTIONAL ────────────────────────────────────
 * Generation is an AI call: it can be over budget, rate-limited or simply
 * unavailable, and the household's allergens are enforced in code AFTER the
 * model answers, so a generation can also be legitimately rejected. The build
 * therefore treats every recipe as optional. Whatever succeeded is kept, the
 * user is told plainly what did and didn't happen, and they always land
 * somewhere useful. There is no path through this screen that strands someone.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { AiApi, CatalogApi, PlanApi, RecipesApi, SettingsApi, ShoppingApi } from "../../api/endpoints.ts";
import { addDays } from "../../lib/dates.ts";
import { draftToBody } from "../../lib/draftToBody.ts";
import { useToday } from "../../lib/useToday.tsx";
import { Banner, ErrorBox, Loading, Spinner } from "../../components/ui.tsx";
import { Icon } from "../../components/Icon.tsx";
import { usePrefs } from "../../i18n/index.tsx";

/** How many dinners we draft. Three is a week's worth of decisions, not seven. */
const RECIPES_TO_DRAFT = 3;

const AMBITION = [
  {
    key: "simple",
    title: "Keep it simple",
    body: "Quick, few ingredients, nothing fussy.",
    maxMinutes: 30,
    moods: ["quick and lazy", "one pan"],
  },
  {
    key: "mixed",
    title: "A bit of both",
    body: "Easy most nights, something nicer sometimes.",
    maxMinutes: 45,
    moods: ["comforting", "hearty"],
  },
  {
    key: "keen",
    title: "We like to cook",
    body: "Happy to spend time and try new things.",
    maxMinutes: undefined,
    moods: ["something special", "spicy"],
  },
] as const;

const CUISINES = [
  "Italian", "Indian", "Mexican", "Chinese", "Thai", "Japanese",
  "French", "Greek", "Middle Eastern", "British", "Spanish", "Vietnamese",
];

interface Eater {
  name: string;
  allergens: string[];
}

type BuildStep = "eaters" | "recipes" | "plan" | "shopping";

interface BuildState {
  step: BuildStep;
  /** Recipes saved so far, of RECIPES_TO_DRAFT. */
  made: number;
  notes: string[];
  failed: boolean;
}

export function FirstRunWizard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { lang } = usePrefs();
  const { today } = useToday();

  const [stage, setStage] = useState<1 | 2 | 3>(1);
  const [eaters, setEaters] = useState<Eater[]>([{ name: "", allergens: [] }]);
  const [ambition, setAmbition] = useState<typeof AMBITION[number]["key"]>("mixed");
  const [cuisines, setCuisines] = useState<string[]>([]);
  const [build, setBuild] = useState<BuildState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const catalog = useQuery({
    queryKey: ["diet-options", lang],
    queryFn: () => CatalogApi.dietOptions(lang),
    staleTime: 60 * 60 * 1000,
  });
  const allergenOptions = catalog.data?.allergens ?? [];

  const named = eaters.filter((e) => e.name.trim());
  const chosen = AMBITION.find((a) => a.key === ambition)!;

  const setEater = (i: number, patch: Partial<Eater>) =>
    setEaters((xs) => xs.map((e, j) => (j === i ? { ...e, ...patch } : e)));

  const toggleAllergen = (i: number, key: string) =>
    setEaters((xs) => xs.map((e, j) => (j === i
      ? {
          ...e,
          allergens: e.allergens.includes(key)
            ? e.allergens.filter((a) => a !== key)
            : [...e.allergens, key],
        }
      : e)));

  // ── the build ─────────────────────────────────────────────────────────────
  const run = async () => {
    if (!today) return;
    setError(null);
    const notes: string[] = [];

    // 1. Eaters. These are the only thing we refuse to continue without: the
    //    allergen checking that runs on every recipe and every plan entry has
    //    nothing to work from until these exist.
    setBuild({ step: "eaters", made: 0, notes, failed: false });
    try {
      for (const e of named) {
        await SettingsApi.addDiet({
          name: e.name.trim(),
          allergens: e.allergens,
          dietTags: [],
          dislikes: [],
          countsAsServings: 1,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save who's eating.");
      setBuild(null);
      return;
    }

    const householdAllergens = [...new Set(named.flatMap((e) => e.allergens))];
    const servings = Math.max(1, named.length);

    // 2. Recipes. Sequential, not parallel: three concurrent generations is
    //    what trips the server's rate limit, and the progress readout is more
    //    honest when it reflects work that has actually finished.
    setBuild({ step: "recipes", made: 0, notes, failed: false });
    const savedIds: string[] = [];
    for (let i = 0; i < RECIPES_TO_DRAFT; i++) {
      try {
        const { draft } = await AiApi.generate({
          servings,
          mealSlot: "dinner",
          mood: chosen.moods.join(", "),
          maxMinutes: chosen.maxMinutes,
          cuisine: cuisines.length ? cuisines[i % cuisines.length] : undefined,
          dietTags: [],
          allergens: householdAllergens,
          mustUse: [],
          dislikes: [],
          avoidRecent: false,
        });
        const saved = await RecipesApi.create(draftToBody(draft, { source: "ai" }));
        savedIds.push(saved.id);
        setBuild({ step: "recipes", made: savedIds.length, notes, failed: false });
      } catch {
        // Deliberately swallowed per-recipe. One rejected generation should not
        // cost the user the other two, the eaters they just entered, or the
        // plan. It is reported honestly at the end.
        notes.push("One recipe couldn't be generated.");
      }
    }

    if (savedIds.length === 0) {
      // Nothing to plan and nothing to shop for, but the eaters were saved and
      // that is real progress. Send them somewhere they can act.
      setBuild(null);
      qc.clear();
      navigate("/recipes?add=1", { replace: true });
      return;
    }

    // 3. Plan — one per day starting today, dinner.
    setBuild({ step: "plan", made: savedIds.length, notes, failed: false });
    let planned = 0;
    for (const [i, recipeId] of savedIds.entries()) {
      try {
        await PlanApi.add({
          planDate: addDays(today, i),
          slotKey: "dinner",
          recipeId,
          servings,
        });
        planned++;
      } catch {
        notes.push("One meal couldn't be added to the plan.");
      }
    }

    // 4. Shopping list, covering exactly the days we planned.
    if (planned > 0) {
      setBuild({ step: "shopping", made: savedIds.length, notes, failed: false });
      try {
        await ShoppingApi.generate({
          from: today,
          to: addDays(today, planned - 1),
          applyStaples: true,
          applyInventory: false,
        });
      } catch {
        notes.push("The shopping list didn't build — you can make it from the Shopping tab.");
      }
    }

    // Everything on screen is now stale: settings, recipes, plan, shopping and
    // the home payload all changed underneath the cache.
    qc.clear();
    navigate("/plan?welcome=1", { replace: true });
  };

  if (!today) return <Loading label="Getting ready…" />;

  // ── the build screen ──────────────────────────────────────────────────────
  if (build) {
    const steps: Array<{ key: BuildStep; label: string }> = [
      { key: "eaters", label: "Saving who's eating" },
      { key: "recipes", label: `Writing ${RECIPES_TO_DRAFT} recipes` },
      { key: "plan", label: "Putting them in the week" },
      { key: "shopping", label: "Building the shopping list" },
    ];
    const at = steps.findIndex((s) => s.key === build.step);

    return (
      <div className="mx-auto max-w-md py-10">
        <h1 className="type-display">Setting up your kitchen</h1>
        <p className="mt-1 text-sm text-ink-2">
          This takes about a minute. The recipes are being written now, so it's worth the wait.
        </p>

        <ol className="mt-6 space-y-3">
          {steps.map((s, i) => (
            <li key={s.key} className="flex items-center gap-3">
              <span className="grid h-6 w-6 shrink-0 place-items-center">
                {i < at
                  ? <Icon name="check" className="h-4 w-4 text-ok" />
                  : i === at
                    ? <Spinner className="h-4 w-4 text-accent" />
                    : <Icon name="circle" className="h-4 w-4 text-ink-3 opacity-40" />}
              </span>
              <span className={i <= at ? "text-ink" : "text-ink-3"}>
                {s.label}
                {s.key === "recipes" && i === at && build.made > 0 && (
                  <span className="text-ink-3"> — {build.made} of {RECIPES_TO_DRAFT} done</span>
                )}
              </span>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  // ── the questions ─────────────────────────────────────────────────────────
  return (
    <div className="mx-auto max-w-lg animate-fade-in py-6">
      <div className="mb-6">
        <div className="label-mono text-accent">Step {stage} of 3</div>
        <div className="mt-2 h-1 overflow-hidden rounded-full bg-paper-3"
             role="progressbar" aria-valuenow={stage} aria-valuemin={1} aria-valuemax={3}>
          <div className="h-full bg-accent transition-all" style={{ width: `${(stage / 3) * 100}%` }} />
        </div>
      </div>

      {error && <div className="mb-4"><ErrorBox message={error} /></div>}

      {/* ── 1. Who's eating ────────────────────────────────────────────── */}
      {stage === 1 && (
        <div className="space-y-4">
          <div>
            <h1 className="type-display">Who's eating?</h1>
            <p className="mt-1 text-sm text-ink-2">
              Everyone the plan is for. Add allergies now and the app will warn you before
              you cook something that contains one — it's the most useful thing you can
              tell it.
            </p>
          </div>

          <div className="space-y-3">
            {eaters.map((e, i) => (
              <div key={i} className="card space-y-2.5 p-4">
                <div className="flex gap-2">
                  <input
                    className="input"
                    placeholder={i === 0 ? "Your name" : "Name"}
                    value={e.name}
                    autoFocus={i === 0}
                    onChange={(ev) => setEater(i, { name: ev.target.value })}
                    aria-label={`Person ${i + 1} name`}
                  />
                  {eaters.length > 1 && (
                    <button
                      className="btn-ghost shrink-0 !px-2.5"
                      aria-label={`Remove person ${i + 1}`}
                      onClick={() => setEaters((xs) => xs.filter((_, j) => j !== i))}
                    >
                      <Icon name="x" className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {allergenOptions.length > 0 && (
                  <details open={e.allergens.length > 0}>
                    <summary className="cursor-pointer text-xs text-ink-3 hover:text-ink">
                      {e.allergens.length > 0
                        ? `Avoiding ${e.allergens.length}`
                        : "Any allergies?"}
                    </summary>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {allergenOptions.map((o) => (
                        <button
                          key={o.key}
                          type="button"
                          title={o.description}
                          aria-pressed={e.allergens.includes(o.key)}
                          onClick={() => toggleAllergen(i, o.key)}
                          className={`chip transition ${
                            e.allergens.includes(o.key)
                              ? "border-danger bg-danger/15 text-danger"
                              : "hover:border-danger/40"}`}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ))}
          </div>

          <button className="btn-ghost w-full"
                  onClick={() => setEaters((xs) => [...xs, { name: "", allergens: [] }])}>
            <Icon name="plus" className="h-4 w-4" />
            Add another person
          </button>

          <div className="flex gap-2 pt-2">
            <button className="btn-primary flex-1" disabled={named.length === 0}
                    onClick={() => setStage(2)}>
              Continue
            </button>
          </div>
          <SkipLink />
        </div>
      )}

      {/* ── 2. Ambition ────────────────────────────────────────────────── */}
      {stage === 2 && (
        <div className="space-y-4">
          <div>
            <h1 className="type-display">How much cooking?</h1>
            <p className="mt-1 text-sm text-ink-2">
              Just a starting point — every recipe can be changed, and you can ask for
              something different any time.
            </p>
          </div>

          <div className="space-y-2">
            {AMBITION.map((a) => (
              <button
                key={a.key}
                type="button"
                onClick={() => setAmbition(a.key)}
                aria-pressed={ambition === a.key}
                className={`flex w-full items-start gap-3 rounded-xl border p-4 text-left transition ${
                  ambition === a.key
                    ? "border-accent bg-accent/5"
                    : "border-rule-soft hover:border-accent/50"}`}
              >
                <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border ${
                  ambition === a.key ? "border-accent bg-accent text-paper" : "border-rule"}`}>
                  {ambition === a.key && <Icon name="check" className="h-3 w-3" />}
                </span>
                <span>
                  <span className="block font-medium text-ink">{a.title}</span>
                  <span className="block text-sm text-ink-3">{a.body}</span>
                </span>
              </button>
            ))}
          </div>

          <div className="flex gap-2 pt-2">
            <button className="btn-ghost" onClick={() => setStage(1)}>Back</button>
            <button className="btn-primary flex-1" onClick={() => setStage(3)}>Continue</button>
          </div>
          <SkipLink />
        </div>
      )}

      {/* ── 3. Cuisines ────────────────────────────────────────────────── */}
      {stage === 3 && (
        <div className="space-y-4">
          <div>
            <h1 className="type-display">What do you like?</h1>
            <p className="mt-1 text-sm text-ink-2">
              Pick a few, or none at all. This only nudges the first three recipes.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {CUISINES.map((c) => (
              <button key={c} type="button" className="chip-toggle"
                      aria-pressed={cuisines.includes(c)}
                      onClick={() => setCuisines((xs) =>
                        xs.includes(c) ? xs.filter((x) => x !== c) : [...xs, c])}>
                {c}
              </button>
            ))}
          </div>

          <Banner>
            Next, {RECIPES_TO_DRAFT} recipes get written for {named.length || "your"}{" "}
            {named.length === 1 ? "person" : "people"}, added to the next{" "}
            {RECIPES_TO_DRAFT} days, and turned into a shopping list. You can change
            any of it afterwards.
          </Banner>

          <div className="flex gap-2 pt-2">
            <button className="btn-ghost" onClick={() => setStage(2)}>Back</button>
            <button className="btn-primary flex-1" onClick={() => void run()}>
              <Icon name="sparkles" className="h-4 w-4" />
              Build my week
            </button>
          </div>
          <SkipLink />
        </div>
      )}
    </div>
  );
}

/**
 * The way out.
 *
 * Present on every stage and never disguised. An onboarding flow you cannot
 * leave is a trap, and someone who already knows what they want should not have
 * to answer three questions to reach an app they have decided to use.
 */
function SkipLink() {
  const navigate = useNavigate();
  return (
    <p className="pt-2 text-center">
      <button className="text-xs text-ink-3 underline hover:text-ink"
              onClick={() => navigate("/", { replace: true })}>
        Skip — I'll set things up myself
      </button>
    </p>
  );
}
